import { useState, useMemo } from 'react';
import { colors, Card, EmptyState, LoadingSpinner } from '../../shared';
import { useData } from '../../../contexts/DataContext';
import {
  BarChart3, Users, UserCheck, UserPlus, Percent, Lock, MessageSquare,
  Receipt, ClipboardList, FileText, ChevronDown, ChevronUp, ArrowUpDown
} from 'lucide-react';

// Stat card for user engagement metrics
const StatCard = ({ icon: Icon, label, value, subValue, color = colors.yellow }) => (
  <Card>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>{label}</p>
        <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{value}</p>
        {subValue && (
          <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{subValue}</p>
        )}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={20} style={{ color }} />
      </div>
    </div>
  </Card>
);

// Horizontal bar for feature usage
const FeatureBar = ({ icon: Icon, label, value, maxValue, color = colors.yellow }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="py-3">
      <div className="flex items-center justify-between text-sm mb-2">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color }} />
          <span style={{ color: colors.textSecondary }}>{label}</span>
        </div>
        <span className="font-medium" style={{ color: colors.textPrimary }}>
          {value.toLocaleString()}
        </span>
      </div>
      <div className="h-3 rounded-full" style={{ background: colors.inputBg }}>
        <div
          className="h-3 rounded-full transition-all"
          style={{ width: `${Math.max(percentage, 2)}%`, background: color }}
        />
      </div>
    </div>
  );
};

// Sortable table header
const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
  const isActive = currentSort.key === sortKey;
  return (
    <button
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 text-left hover:opacity-80"
      style={{ color: isActive ? colors.yellow : colors.textMuted }}
    >
      {label}
      {isActive ? (
        currentSort.asc ? <ChevronUp size={14} /> : <ChevronDown size={14} />
      ) : (
        <ArrowUpDown size={12} className="opacity-50" />
      )}
    </button>
  );
};

// Format relative time
const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = date?.toDate?.() || new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
};

export default function AnalyticsTab({ permissions }) {
  const { users, lockChanges, consultSessions, receipts, inspections, insuranceEstimates, loading } = useData();
  const [timeRange, setTimeRange] = useState('7d');
  const [sort, setSort] = useState({ key: 'locks', asc: false });

  // Calculate time range filter
  const getDateThreshold = () => {
    const now = new Date();
    const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 1;
    return new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
  };

  // Filter items by date
  const filterByDate = (items, dateField = 'createdAt') => {
    const threshold = getDateThreshold();
    return (items || []).filter(item => {
      const itemDate = item[dateField]?.toDate?.() || new Date(item[dateField]);
      return itemDate >= threshold;
    });
  };

  // Calculate user engagement stats
  const userStats = useMemo(() => {
    const threshold = getDateThreshold();
    const allUsers = users || [];

    const activeUsers = allUsers.filter(user => {
      const lastLogin = user.lastLoginAt?.toDate?.() || new Date(user.lastLoginAt);
      return lastLogin >= threshold;
    });

    const newUsers = allUsers.filter(user => {
      const createdAt = user.createdAt?.toDate?.() || new Date(user.createdAt);
      return createdAt >= threshold;
    });

    const loginRate = allUsers.length > 0
      ? ((activeUsers.length / allUsers.length) * 100).toFixed(0)
      : 0;

    return {
      total: allUsers.length,
      active: activeUsers.length,
      new: newUsers.length,
      loginRate,
    };
  }, [users, timeRange]);

  // Calculate feature usage stats
  const featureStats = useMemo(() => {
    return {
      locks: filterByDate(lockChanges).length,
      consults: filterByDate(consultSessions).length,
      receipts: filterByDate(receipts).length,
      inspections: filterByDate(inspections).length,
      estimates: filterByDate(insuranceEstimates).length,
    };
  }, [lockChanges, consultSessions, receipts, inspections, insuranceEstimates, timeRange]);

  // Calculate per-tech activity
  const techActivity = useMemo(() => {
    const threshold = getDateThreshold();
    const activity = {};

    // Initialize with all users
    (users || []).forEach(user => {
      activity[user.id] = {
        id: user.id,
        name: user.name || user.email?.split('@')[0] || 'Unknown',
        email: user.email,
        locks: 0,
        consults: 0,
        receipts: 0,
        inspections: 0,
        lastActive: user.lastLoginAt,
      };
    });

    // Count lock changes
    filterByDate(lockChanges).forEach(item => {
      if (item.techId && activity[item.techId]) {
        activity[item.techId].locks++;
      }
    });

    // Count consult sessions
    filterByDate(consultSessions).forEach(item => {
      if (item.techId && activity[item.techId]) {
        activity[item.techId].consults++;
      }
    });

    // Count receipts
    filterByDate(receipts).forEach(item => {
      if (item.techId && activity[item.techId]) {
        activity[item.techId].receipts++;
      }
    });

    // Count inspections
    filterByDate(inspections).forEach(item => {
      if (item.techId && activity[item.techId]) {
        activity[item.techId].inspections++;
      }
    });

    // Convert to array and sort
    let result = Object.values(activity);

    result.sort((a, b) => {
      let valA, valB;
      if (sort.key === 'name') {
        valA = a.name.toLowerCase();
        valB = b.name.toLowerCase();
        return sort.asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      } else if (sort.key === 'lastActive') {
        valA = a.lastActive?.toDate?.() || new Date(a.lastActive || 0);
        valB = b.lastActive?.toDate?.() || new Date(b.lastActive || 0);
        return sort.asc ? valA - valB : valB - valA;
      } else {
        valA = a[sort.key] || 0;
        valB = b[sort.key] || 0;
        return sort.asc ? valA - valB : valB - valA;
      }
    });

    return result;
  }, [users, lockChanges, consultSessions, receipts, inspections, timeRange, sort]);

  // Calculate daily activity trends
  const dailyTrends = useMemo(() => {
    const threshold = getDateThreshold();
    const days = {};

    // Initialize days
    const now = new Date();
    const daysCount = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 1;
    for (let i = 0; i < daysCount; i++) {
      const date = new Date(now.getTime() - i * 86400000).toISOString().split('T')[0];
      days[date] = { date, locks: 0, consults: 0, receipts: 0, inspections: 0 };
    }

    // Count by feature per day
    const countByDay = (items, feature) => {
      filterByDate(items).forEach(item => {
        const date = (item.createdAt?.toDate?.() || new Date(item.createdAt)).toISOString().split('T')[0];
        if (days[date]) {
          days[date][feature]++;
        }
      });
    };

    countByDay(lockChanges, 'locks');
    countByDay(consultSessions, 'consults');
    countByDay(receipts, 'receipts');
    countByDay(inspections, 'inspections');

    return Object.values(days).sort((a, b) => a.date.localeCompare(b.date));
  }, [lockChanges, consultSessions, receipts, inspections, timeRange]);

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      asc: prev.key === key ? !prev.asc : false,
    }));
  };

  if (!permissions?.viewAnalytics) {
    return (
      <div className="p-6">
        <EmptyState
          icon={BarChart3}
          title="Access Denied"
          description="You don't have permission to view analytics"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const maxFeature = Math.max(
    featureStats.locks,
    featureStats.consults,
    featureStats.receipts,
    featureStats.inspections,
    featureStats.estimates,
    1
  );

  const maxDailyTotal = Math.max(
    ...dailyTrends.map(d => d.locks + d.consults + d.receipts + d.inspections),
    1
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
            User Performance Dashboard
          </h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            User engagement and feature usage metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['1d', '7d', '30d'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: timeRange === range ? colors.yellowDim : colors.inputBg,
                color: timeRange === range ? colors.yellow : colors.textMuted,
              }}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* User Engagement Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={userStats.total}
          color={colors.yellow}
        />
        <StatCard
          icon={UserCheck}
          label="Active Users"
          value={userStats.active}
          subValue={`Logged in within ${timeRange}`}
          color={colors.green}
        />
        <StatCard
          icon={UserPlus}
          label="New Users"
          value={userStats.new}
          subValue={`Created within ${timeRange}`}
          color={colors.blue}
        />
        <StatCard
          icon={Percent}
          label="Login Rate"
          value={`${userStats.loginRate}%`}
          subValue="Active / Total"
          color={colors.orange}
        />
      </div>

      {/* Feature Usage */}
      <Card>
        <h2 className="text-lg font-bold mb-4" style={{ color: colors.textPrimary }}>
          Feature Usage
        </h2>
        <div className="space-y-1">
          <FeatureBar
            icon={Lock}
            label="Lock Changes"
            value={featureStats.locks}
            maxValue={maxFeature}
            color={colors.yellow}
          />
          <FeatureBar
            icon={MessageSquare}
            label="AI Consults"
            value={featureStats.consults}
            maxValue={maxFeature}
            color={colors.green}
          />
          <FeatureBar
            icon={Receipt}
            label="Receipts Uploaded"
            value={featureStats.receipts}
            maxValue={maxFeature}
            color={colors.blue}
          />
          <FeatureBar
            icon={ClipboardList}
            label="Inspections"
            value={featureStats.inspections}
            maxValue={maxFeature}
            color={colors.orange}
          />
          <FeatureBar
            icon={FileText}
            label="Insurance Estimates"
            value={featureStats.estimates}
            maxValue={maxFeature}
            color={colors.purple || '#a855f7'}
          />
        </div>
      </Card>

      {/* Per-Tech Activity Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>
            Per-Tech Activity
          </h2>
          <span className="text-sm" style={{ color: colors.textMuted }}>
            {techActivity.length} technicians
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th className="text-left py-3 px-2">
                  <SortableHeader label="Tech Name" sortKey="name" currentSort={sort} onSort={handleSort} />
                </th>
                <th className="text-center py-3 px-2">
                  <SortableHeader label="Locks" sortKey="locks" currentSort={sort} onSort={handleSort} />
                </th>
                <th className="text-center py-3 px-2">
                  <SortableHeader label="Consults" sortKey="consults" currentSort={sort} onSort={handleSort} />
                </th>
                <th className="text-center py-3 px-2">
                  <SortableHeader label="Receipts" sortKey="receipts" currentSort={sort} onSort={handleSort} />
                </th>
                <th className="text-center py-3 px-2">
                  <SortableHeader label="Inspections" sortKey="inspections" currentSort={sort} onSort={handleSort} />
                </th>
                <th className="text-right py-3 px-2">
                  <SortableHeader label="Last Active" sortKey="lastActive" currentSort={sort} onSort={handleSort} />
                </th>
              </tr>
            </thead>
            <tbody>
              {techActivity.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8" style={{ color: colors.textMuted }}>
                    No technician data available
                  </td>
                </tr>
              ) : (
                techActivity.map(tech => (
                  <tr
                    key={tech.id}
                    className="hover:bg-white/5 transition-colors"
                    style={{ borderBottom: `1px solid ${colors.border}` }}
                  >
                    <td className="py-3 px-2">
                      <div>
                        <p className="font-medium" style={{ color: colors.textPrimary }}>{tech.name}</p>
                        <p className="text-xs" style={{ color: colors.textMuted }}>{tech.email}</p>
                      </div>
                    </td>
                    <td className="text-center py-3 px-2" style={{ color: tech.locks > 0 ? colors.textPrimary : colors.textMuted }}>
                      {tech.locks}
                    </td>
                    <td className="text-center py-3 px-2" style={{ color: tech.consults > 0 ? colors.textPrimary : colors.textMuted }}>
                      {tech.consults}
                    </td>
                    <td className="text-center py-3 px-2" style={{ color: tech.receipts > 0 ? colors.textPrimary : colors.textMuted }}>
                      {tech.receipts}
                    </td>
                    <td className="text-center py-3 px-2" style={{ color: tech.inspections > 0 ? colors.textPrimary : colors.textMuted }}>
                      {tech.inspections}
                    </td>
                    <td className="text-right py-3 px-2" style={{ color: colors.textMuted }}>
                      {formatRelativeTime(tech.lastActive)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Activity Trends */}
      <Card>
        <h2 className="text-lg font-bold mb-4" style={{ color: colors.textPrimary }}>
          Activity Trends ({timeRange})
        </h2>
        {dailyTrends.length === 0 ? (
          <p className="text-sm" style={{ color: colors.textMuted }}>No trend data available</p>
        ) : (
          <>
            <div className="flex items-end gap-1 h-32">
              {dailyTrends.map((day) => {
                const total = day.locks + day.consults + day.receipts + day.inspections;
                const height = (total / maxDailyTotal) * 100;

                // Stack the bars
                const locksHeight = total > 0 ? (day.locks / total) * height : 0;
                const consultsHeight = total > 0 ? (day.consults / total) * height : 0;
                const receiptsHeight = total > 0 ? (day.receipts / total) * height : 0;
                const inspectionsHeight = total > 0 ? (day.inspections / total) * height : 0;

                return (
                  <div
                    key={day.date}
                    className="flex-1 flex flex-col justify-end rounded-t overflow-hidden cursor-pointer hover:opacity-80"
                    style={{ height: `${Math.max(height, 4)}%`, minHeight: '4px' }}
                    title={`${day.date}\nLocks: ${day.locks}\nConsults: ${day.consults}\nReceipts: ${day.receipts}\nInspections: ${day.inspections}`}
                  >
                    {inspectionsHeight > 0 && (
                      <div style={{ height: `${(inspectionsHeight / height) * 100}%`, background: colors.orange, minHeight: '2px' }} />
                    )}
                    {receiptsHeight > 0 && (
                      <div style={{ height: `${(receiptsHeight / height) * 100}%`, background: colors.blue, minHeight: '2px' }} />
                    )}
                    {consultsHeight > 0 && (
                      <div style={{ height: `${(consultsHeight / height) * 100}%`, background: colors.green, minHeight: '2px' }} />
                    )}
                    {locksHeight > 0 && (
                      <div style={{ height: `${(locksHeight / height) * 100}%`, background: colors.yellow, minHeight: '2px' }} />
                    )}
                    {total === 0 && (
                      <div style={{ height: '100%', background: colors.inputBg }} />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-xs mt-2" style={{ color: colors.textMuted }}>
              <span>{dailyTrends[0]?.date || ''}</span>
              <span>{dailyTrends[dailyTrends.length - 1]?.date || ''}</span>
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 text-xs" style={{ color: colors.textMuted }}>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ background: colors.yellow }} />
                <span>Locks</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ background: colors.green }} />
                <span>Consults</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ background: colors.blue }} />
                <span>Receipts</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ background: colors.orange }} />
                <span>Inspections</span>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
